import { useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { EMPTY_STRING } from "src/components/constant/constant";
import styles from "./shortIdChip.module.scss";

export interface ShortIdChipProps {
  id?: string | null;
  leading?: number;
  trailing?: number;
  withCopy?: boolean;
  withTooltip?: boolean;
  variant?: "pill" | "ghost";
  tooltipLabel?: string;
  copiedLabel?: string;
  fallback?: string;
  className?: string;
}

function shorten(id: string, leading: number, trailing: number) {
  if (!id) return EMPTY_STRING;
  if (id.length <= leading + trailing + 1) return id;
  return `${id.slice(0, leading)}…${id.slice(-trailing)}`;
}

export function ShortIdChip({
  id,
  leading = 6,
  trailing = 4,
  withCopy = true,
  withTooltip = true,
  variant = "pill",
  tooltipLabel,
  copiedLabel = "Copied!",
  fallback = EMPTY_STRING,
  className
}: ShortIdChipProps) {
  const [copied, setCopied] = useState(false);

  const shortId = useMemo(() => shorten(id ?? "", leading, trailing), [id, leading, trailing]);

  if (!id) return <span className={styles.fallback}>{fallback}</span>;

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const chipClass = [
    styles.chip,
    variant === "ghost" ? styles.chipGhost : styles.chipPill,
    !withCopy && styles.chipReadOnly,
    className
  ]
    .filter(Boolean)
    .join(" ");

  const chipBody = (
    <span className={chipClass}>
      <span className={styles.idText}>{shortId}</span>
      {withCopy && (
        <span className={styles.iconSlot} aria-hidden>
          {copied ? <CheckOutlinedIcon fontSize="inherit" /> : <ContentCopyOutlinedIcon fontSize="inherit" />}
        </span>
      )}
    </span>
  );

  const tooltipTitle = copied ? copiedLabel : (tooltipLabel ?? id);

  const content = withCopy ? (
    <CopyToClipboard text={id} onCopy={handleCopy}>
      {chipBody}
    </CopyToClipboard>
  ) : (
    chipBody
  );

  if (!withTooltip) {
    return <span className={styles.wrapper}>{content}</span>;
  }

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <span className={styles.wrapper}>{content}</span>
    </Tooltip>
  );
}
